import { CSSProperties, useMemo, useState } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import MarkdownPreview from '@uiw/react-markdown-preview';

import VoteMateLogo from '@/ui-library/VoteMateLogo';
import { primitiveColors, semanticColors, BORDER_RADIUS } from '@/theme/tokens';

import { ChatMessage, MessageActions } from '../ChatComponents';
import { MinimalThinkingState } from '../MinimalThinkingState';
import NarrationTimeline from '../NarrationTimeline';
import ReasoningDisclosure from '../ReasoningDisclosure';
import { hasResearchTrail } from '../NarrationTypes';
import {
  repairCitations,
  sanitizeSources,
  normalizeUrl,
  urlDedupKey,
  getPublisherName,
  getFaviconUrl,
  groupSources,
} from '../SourceTypes';
import type { SourceItem } from '../SourceTypes';

// Horizontal padding (in MUI spacing units) of the message body Paper.
// The "Memory updated" pill aligns its left edge to the body by reusing
// this same value for `mx`; keep them in sync via this constant.
const MESSAGE_BODY_PX = 2;

// Private-Use-Area sentinel pair that wraps citation numbers in the markdown
// source between text-replacement (below) and AST expansion (in rehypeRewrite).
// PUA code points have no special meaning in any markdown construct (tables,
// links, code spans, autolinks), so the pair `\uE000N\uE001` survives parsing
// as plain text and lands in a single HAST text node we can rewrite. Doing
// this substitution at the AST level — rather than injecting raw <a> HTML
// into the markdown source — is what allows citations to render correctly
// inside GFM table cells, where inline HTML gets mangled by the
// autolink-literal extension and by literal "|" being read as the column
// separator. See VoteMate issue #1777.
const CITE_OPEN = '\uE000';
const CITE_CLOSE = '\uE001';
const CITE_SENTINEL_RE = /\uE000(\d+)\uE001/g;

/** Shared styling for the citation hover/focus preview popper. */
const CITATION_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: primitiveColors.white,
      color: primitiveColors.rockSlate,
      border: '1px solid #E6E8EB',
      borderRadius: 2,
      boxShadow: '0 4px 16px rgba(12,47,55,0.14)',
      p: 1,
      maxWidth: 280,
    },
  },
  arrow: {
    sx: { color: primitiveColors.white, '&::before': { border: '1px solid #E6E8EB' } },
  },
} as const;

/** Hover/focus preview shown for an inline citation chip: the source's
 *  article title with a favicon + publisher line beneath it. */
const CitationPreview: React.FC<{ source?: SourceItem }> = ({ source }) => {
  const favicon = getFaviconUrl(source?.url);
  const publisher = source ? getPublisherName(source) : '';
  const title = (source?.title || '').trim();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 260 }}>
      {title && (
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.35,
            color: primitiveColors.rockSlate,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {favicon && (
          <Box
            component="img"
            src={favicon}
            alt=""
            aria-hidden="true"
            sx={{ width: 14, height: 14, borderRadius: '3px', objectFit: 'contain' }}
          />
        )}
        <Typography sx={{ fontSize: 11, fontWeight: 500, color: `${primitiveColors.rockSlate}99` }}>
          {publisher}
        </Typography>
      </Box>
    </Box>
  );
};

// Coarse pointer (touch) has no hover, so a tap on a citation would navigate
// instantly. On touch we make the first tap reveal the preview and the second
// follow the link (mobile-app parity); hover devices keep click-through.
const IS_TOUCH =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: none)').matches;

// Minimal HAST shapes we touch; `any` keeps parity with rehype-rewrite's
// loosely-typed node callback without pulling in the full hast types.
type CiteHastNode = any;
type Translate = (key: string, opts?: Record<string, unknown>) => string;

// Citation chips are emitted into the HAST as empty placeholder <span>s and
// rendered as React by the `span` override in markdownComponents. The citation
// numbers ride in the className list rather than a data-* attribute: className
// is the one property this markdown pipeline is already known to round-trip end
// to end, so nothing here rests on the renderer's policy for custom attributes.
// Verified to survive in paragraphs, list items, and table cells — the last of
// which is where the previous inline-HTML approach broke (see #1777).
const CITE_REF_CLASS = 'vm-cite-ref';
const CITE_NUM_CLASS_RE = /^vm-cite-n(\d+)$/;

// Markers, a trailing run of them, and a list-item opener — for the lead-in
// de-duplication below.
const CITE_MARKER_RE = /\[(\d+)\](?!\()/g;
const TRAILING_CITE_RUN_RE = /(?:\s*\[\d+\](?!\())+\s*$/;
const LIST_ITEM_RE = /^\s*(?:[-*+]|\d+[.)])\s+/;

/** Drop a lead-in sentence's citations when the list right below re-cites them
 *  all.
 *
 *  "Ten endorsements are recorded for Lieu:" followed by ten bullets, each with
 *  its own citation, arrives from the model with the full set on the lead-in
 *  line *and* one per bullet — so every source renders twice, the first time as
 *  a block of chips wrapping across several lines above the list. Publisher
 *  clustering doesn't help here: ten endorsers are usually ten different
 *  publishers, so it yields ten clusters of one.
 *
 *  Only strips when every lead-in citation reappears below, so a source that
 *  supports the sentence alone keeps its chip. The prompt asks for this shape
 *  directly; this is the deterministic backstop for when the model repeats
 *  itself anyway. It lives client-side because the backend streams the lead-in
 *  before the list it introduces exists. */
function dropRedundantLeadInCitations(text: string): string {
  const lines = text.split('\n');

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (LIST_ITEM_RE.test(line)) continue;

    const run = line.match(TRAILING_CITE_RUN_RE);
    if (!run) continue;
    const leadNums = new Set<string>();
    for (const m of run[0].matchAll(CITE_MARKER_RE)) leadNums.add(m[1]);
    if (leadNums.size === 0) continue;

    // The next non-blank line has to open a list for this to be a lead-in.
    let j = i + 1;
    while (j < lines.length && lines[j].trim() === '') j += 1;
    if (j >= lines.length || !LIST_ITEM_RE.test(lines[j])) continue;

    const listNums = new Set<string>();
    for (; j < lines.length; j += 1) {
      const candidate = lines[j];
      if (candidate.trim() === '') continue; // loose lists blank-separate items
      // A line that neither opens an item nor continues one ends the list.
      if (!LIST_ITEM_RE.test(candidate) && !/^\s+\S/.test(candidate)) break;
      for (const m of candidate.matchAll(CITE_MARKER_RE)) listNums.add(m[1]);
    }

    let coveredBelow = true;
    leadNums.forEach((n) => {
      if (!listNums.has(n)) coveredBelow = false;
    });
    if (coveredBelow) lines[i] = line.replace(TRAILING_CITE_RUN_RE, '');
  }

  return lines.join('\n');
}

/** Placeholder node carrying one cluster's citation numbers, in display order. */
function buildCitationHastNode(nums: number[]): CiteHastNode {
  return {
    type: 'element',
    tagName: 'span',
    properties: {
      className: [CITE_REF_CLASS, ...nums.map((n) => `vm-cite-n${n}`)],
    },
    children: [],
  };
}

/** Read a placeholder's citation numbers back out of its class list. Returns
 *  null for any span that isn't one of ours, so ordinary spans pass through. */
function citeNumsFromClassName(className: unknown): number[] | null {
  const classes =
    typeof className === 'string'
      ? className.split(/\s+/)
      : Array.isArray(className)
        ? className.map(String)
        : [];
  if (!classes.includes(CITE_REF_CLASS)) return null;
  const nums: number[] = [];
  for (const cls of classes) {
    const m = CITE_NUM_CLASS_RE.exec(cls);
    if (m) nums.push(parseInt(m[1], 10));
  }
  return nums.length > 0 ? nums : null;
}

/** Split a block's citations into per-publisher clusters, in the order each
 *  publisher first appears.
 *
 *  This is what keeps a roundup from ending in a wall of chips: an endorsement
 *  block citing eight Instagram posts and four Sway pages renders two clusters
 *  rather than twelve near-identical pills wrapping across three lines. Sources
 *  whose publisher can't be resolved stay in clusters of one, so nothing is
 *  merged on a guess. */
function clusterByPublisher(nums: number[], citedSources: SourceItem[]): number[][] {
  const order: string[] = [];
  const byPublisher = new Map<string, number[]>();
  nums.forEach((n, i) => {
    const source = citedSources[n - 1];
    const publisher = source ? getPublisherName(source) : '';
    // Unresolvable publishers key on position so they never merge together.
    const key = publisher || `\u0000${i}`;
    if (!byPublisher.has(key)) {
      byPublisher.set(key, []);
      order.push(key);
    }
    byPublisher.get(key)!.push(n);
  });
  return order.map((key) => byPublisher.get(key)!);
}

/** Group assignment + within-group numbering, memoized per source list.
 *
 *  Every citation in a message asks the same question of the same array, and the
 *  answer only changes when the array does. Keyed on the array identity so the
 *  entry is dropped with the message rather than accumulating. */
const groupingCache = new WeakMap<SourceItem[], ReturnType<typeof groupSources>>();

function grouping(citedSources: SourceItem[]): ReturnType<typeof groupSources> {
  const cached = groupingCache.get(citedSources);
  if (cached) return cached;
  const computed = groupSources(citedSources);
  groupingCache.set(citedSources, computed);
  return computed;
}

/** One citation chip: the publisher's favicon, its name, and — only when the
 *  name alone can't identify which source is meant — a superscript ordinal.
 *
 *  The publisher name leads because "AP News" says where a claim came from,
 *  while "[4]" only means something to someone counting rows in the Sources
 *  panel. The ordinal returns when a message cites two or more distinct pages
 *  from one outlet, which would otherwise render as identical chips pointing at
 *  different articles. It renders as a true superscript rather than the older
 *  bracketed "[4]" so it reads as a reference mark instead of a footnote. */
const CitationChip: React.FC<{
  n: number;
  citedSources: SourceItem[];
  t: Translate;
}> = ({ n, citedSources, t }) => {
  // ``n`` is the per-message citation number, already renumbered 1..M for THIS
  // message (see citationNumberMap), not the internal global ``cite_index``.
  const [previewOpen, setPreviewOpen] = useState(false);
  const source = citedSources[n - 1];
  const href = source?.url || '';
  const publisher = source ? getPublisherName(source) : '';
  const favicon = getFaviconUrl(source?.url);
  // The visible ordinal is the source's position inside its Sources-menu
  // group, not its position in the whole list — small enough to read as a
  // superscript, and it matches the number on the row it points at. Shown only
  // when the same domain is cited more than once, since the publisher name
  // already tells two different sites apart.
  const { displayNumber, sameDomainCount } = grouping(citedSources);
  const shownNumber = displayNumber.get(n - 1) ?? n;
  const label = publisher || `[${shownNumber}]`;
  const showOrdinal = Boolean(publisher) && (sameDomainCount.get(n - 1) ?? 0) > 1;
  const titleForLabel = (source?.title || '').trim() || href;

  const inner = (
    <>
      {favicon && <img className="vm-citation__favicon" src={favicon} alt="" aria-hidden="true" />}
      <span className="vm-citation__label">{label}</span>
      {showOrdinal && <sup className="vm-citation__ordinal">{shownNumber}</sup>}
    </>
  );

  if (!href) {
    return (
      <span
        className="vm-citation vm-citation--unavailable"
        aria-label={t('citation.unavailable', { n })}
      >
        {inner}
      </span>
    );
  }

  return (
    <Tooltip
      placement="top"
      arrow
      enterTouchDelay={0}
      // Keep the tapped preview open until dismissed; MUI's default
      // leaveTouchDelay (1500ms) would otherwise auto-close it on touch.
      leaveTouchDelay={2147483647}
      title={
        // On touch the preview popup sits over the chip, so a second tap lands
        // here rather than on the anchor. Make the popup itself open the source.
        <Box
          {...(IS_TOUCH
            ? {
                role: 'link',
                tabIndex: 0,
                onClick: () => {
                  setPreviewOpen(false);
                  window.open(href, '_blank', 'noopener,noreferrer');
                },
                sx: { cursor: 'pointer' },
              }
            : {})}
        >
          <CitationPreview source={source} />
          {IS_TOUCH && (
            <Typography
              sx={{ mt: 0.75, fontSize: 11, fontWeight: 700, color: primitiveColors.rockSlate }}
            >
              {t('citation.tapToOpen')}
            </Typography>
          )}
        </Box>
      }
      slotProps={CITATION_TOOLTIP_SLOT_PROPS}
      {...(IS_TOUCH ? { open: previewOpen, onClose: () => setPreviewOpen(false) } : {})}
    >
      <a
        className="vm-citation"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('citation.label', { n, title: titleForLabel })}
        onClick={(e) => {
          // Touch: first tap reveals the preview instead of navigating; a second
          // tap follows the link. Hover devices keep native click-through.
          if (!IS_TOUCH) return;
          if (!previewOpen) {
            e.preventDefault();
            setPreviewOpen(true);
          } else {
            // Second tap: open explicitly. MUI's Tooltip touch handling swallows
            // the native anchor navigation on mobile web, so the link never
            // opened; window.open follows it reliably.
            e.preventDefault();
            setPreviewOpen(false);
            window.open(href, '_blank', 'noopener,noreferrer');
          }
        }}
      >
        {inner}
      </a>
    </Tooltip>
  );
};

/** A cluster of citations that all share one publisher, collapsed to a single
 *  counted pill until the reader opens it.
 *
 *  A block citing eight Instagram posts used to render eight near-identical
 *  pills wrapping across three lines; the count carries the same information in
 *  one. The pill stays put when expanded and toggles, so there is a single
 *  control in a single place for both directions.
 *
 *  Deliberately untracked in Mixpanel — decided with the team, so it is not
 *  re-asked on this element. */
const CitationCluster: React.FC<{
  nums: number[];
  citedSources: SourceItem[];
  t: Translate;
}> = ({ nums, citedSources, t }) => {
  const [expanded, setExpanded] = useState(false);

  if (nums.length === 1) {
    return <CitationChip n={nums[0]} citedSources={citedSources} t={t} />;
  }

  const first = citedSources[nums[0] - 1];
  const publisher = first ? getPublisherName(first) : '';
  const favicon = getFaviconUrl(first?.url);
  const label = publisher || t('sources.externalSource');

  return (
    <>
      <button
        type="button"
        className={`vm-citation vm-citation--cluster${expanded ? ' is-expanded' : ''}`}
        aria-expanded={expanded}
        aria-label={
          expanded
            ? t('citation.clusterCollapse', { publisher: label })
            : t('citation.clusterExpand', { publisher: label, count: nums.length })
        }
        onClick={() => setExpanded((open) => !open)}
      >
        {favicon && (
          <img className="vm-citation__favicon" src={favicon} alt="" aria-hidden="true" />
        )}
        <span className="vm-citation__label">{label}</span>
        <span className="vm-citation__count">{nums.length}</span>
        <span className="vm-citation__chevron" aria-hidden="true" />
      </button>
      {expanded &&
        nums.map((n) => <CitationChip key={n} n={n} citedSources={citedSources} t={t} />)}
    </>
  );
};

// Block-level tags a citation belongs to. When gathering a block's own
// citations we stop at these so a nested block (e.g. a <p> inside a loose
// <li>) keeps its citations and renders them at its own end.
const NESTED_BLOCK_TAGS = new Set([
  'p',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  'blockquote',
  'div',
  'pre',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);
// Blocks whose citations get collected, de-duplicated, and moved to the end.
const CITATION_BLOCK_TAGS = new Set(['p', 'li', 'td', 'th']);

/** Pull every citation sentinel out of a block's inline descendants (removing
 *  them from the text), without descending into nested blocks. */
function collectAndStripSentinels(node: CiteHastNode, out: number[], isRoot: boolean): void {
  if (!node || typeof node !== 'object') return;
  if (
    node.type === 'text' &&
    typeof node.value === 'string' &&
    node.value.indexOf(CITE_OPEN) !== -1
  ) {
    node.value = node.value.replace(/ ?(\d+)/g, (_m: string, d: string) => {
      out.push(parseInt(d, 10));
      return '';
    });
    return;
  }
  if (node.type === 'element' && !isRoot && NESTED_BLOCK_TAGS.has(node.tagName)) return;
  if (Array.isArray(node.children)) {
    node.children.forEach((child: CiteHastNode) => collectAndStripSentinels(child, out, false));
  }
}

/** Move a block's citations to a single de-duplicated group at its end so
 *  chips read as a tidy trailing cluster instead of interrupting the prose,
 *  with same-publisher runs collapsed into one counted pill apiece. */
function relocateCitationsToBlockEnd(blockNode: CiteHastNode, citedSources: SourceItem[]): void {
  if (!Array.isArray(blockNode.children)) return;
  const nums: number[] = [];
  collectAndStripSentinels(blockNode, nums, true);
  if (nums.length === 0) return;
  const seen = new Set<number>();
  const unique: number[] = [];
  for (const n of nums) {
    if (!seen.has(n)) {
      seen.add(n);
      unique.push(n);
    }
  }
  blockNode.children.push({ type: 'text', value: ' ' });
  for (const cluster of clusterByPublisher(unique, citedSources)) {
    blockNode.children.push(buildCitationHastNode(cluster));
  }
}

interface BotMessageProps {
  message: ChatMessage;
  onContentUpdate?: () => void;
  onFeedbackSubmit?: (
    messageId: string,
    feedbackType: 'thumbs_up' | 'thumbs_down',
    feedback: string
  ) => void;
  feedbackConfirmations?: { [messageId: string]: 'thumbs_up' | 'thumbs_down' };
  conversationId?: string | null;
  userId?: string;
  streamState?: {
    event: 'start' | 'message' | 'memory_updated' | 'new_conversation' | 'end';
    loading: boolean;
    loadingMessage?: string;
  };
  isLastMessage?: boolean;
  sx?: CSSProperties;
}

export const BotMessage: React.FC<BotMessageProps> = ({
  message,
  onFeedbackSubmit,
  feedbackConfirmations,
  conversationId,
  userId,
  isLastMessage,
  sx,
}) => {
  const { t } = useTranslation('chat');
  // Prefer structured sources from backend; never parse from body
  const parsedMessage = useMemo(() => {
    return {
      content: message.text || '',
      sources: sanitizeSources(message.sources || []),
    };
  }, [message.text, message.sources]);

  // Backend emits event:narration for tool-call status lines and event:message
  // for the actual answer. Read the fields directly — no text parsing needed.
  const narrationEvents = message.narrationEvents || [];
  // Expand any range citations the LLM emitted (e.g. ``[113–127]`` for a run
  // of grounding sources) into individual ``[N]`` markers BEFORE any other
  // citation handling. Caps the expansion span to keep a malformed or
  // hallucinated range from blowing up the body. Hyphen, en-dash, and em-dash
  // are all accepted because the LLM mixes them.
  const bodyText = useMemo(() => {
    const raw = parsedMessage.content || '';
    // Expand range citations (e.g. [113–127]) into individual [N] markers.
    const expanded = raw.replace(/\[(\d+)\s*[-–—]\s*(\d+)\]/g, (m, a, b) => {
      const start = parseInt(a, 10);
      const end = parseInt(b, 10);
      if (!Number.isFinite(start) || !Number.isFinite(end)) return m;
      const lo = Math.min(start, end);
      const hi = Math.max(start, end);
      if (hi - lo > 50) return m; // safety cap
      const parts: string[] = [];
      for (let n = lo; n <= hi; n += 1) parts.push(`[${n}]`);
      return parts.join('');
    });
    // LLMs frequently emit bold pseudo-headers (e.g. **Candidate Name — Race**)
    // after an intro sentence with only a single \n. Markdown treats a single
    // \n as a soft break (inline continuation), so the header renders on the
    // same line as the preceding text. Normalize to \n\n so each bold-start
    // line is parsed as its own paragraph element.
    const spaced = expanded.replace(/([^\n])\n(\*\*)/g, '$1\n\n$2');
    // Every citation is put on a claim its source supports before anything
    // resolves one — see `repairCitations`. Ahead of the lead-in de-dup so that
    // compares the numbers the body will actually render, and ahead of
    // resolution so one pass decides placement for the chips and the panel
    // alike. Reads the raw array, which is what carries `subject` and `topic`.
    const { text: placed } = repairCitations(spaced, message.sources || []);
    // Runs after range expansion so a `[113–127]` lead-in is compared as the
    // individual markers the list below carries.
    return dropRedundantLeadInCitations(placed);
  }, [parsedMessage.content, message.sources]);

  // Renumber [n] citations in body text to a sequential 1..M for display, and
  // build the panel's source list. Walk in body-text occurrence order (not
  // rawSources position) so the reader-visible numbering is monotonic even
  // when the LLM cites raw indices in a non-monotonic order across tools.
  // Falls back to showing every source unchanged if citations exist but none
  // resolve, to avoid silently dropping references.
  const { citedSources, panelSources, citedCount, citationNumberMap, citationsResolved } =
    useMemo(() => {
      const text = bodyText || '';

      // Build a lookup from dedup key → index in the deduplicated source list so
      // citation indices in the raw array map to the correct deduped entry. The
      // key must be the one `sanitizeSources` used, or a URL variant it
      // collapsed misses the lookup and drops its citation. It is also what
      // decides whether two pages are one row at all: `urlDedupKey` keeps the
      // query, so two `watch?v=…` videos stay two sources rather than one
      // crediting the other's claim.
      const dedupedUrlToIdx = new Map<string, number>();
      parsedMessage.sources.forEach((source, idx) => {
        if (source.url) dedupedUrlToIdx.set(urlDedupKey(source.url), idx);
      });

      const rawSources = message.sources || [];
      // Resolve [N] → raw source by the canonical ``cite_index`` assigned by the
      // backend's source-index wrapper. cite_index is the only stable handle when
      // tool results arrive out of order from parallel tool calls.
      //
      // Array position is the LEGACY path, used only when no source carries a
      // cite_index at all. Once the wrapper has stamped the array, [N] means
      // cite_index N and position means nothing — so a positional fallback there
      // does not recover a source the backend failed to send, it credits the
      // claim to an unrelated one: a tool that reserved indices 9-10 and then
      // failed to deliver its sources leaves [9] pointing at whatever else sits
      // at position 9. An unresolved [N] is dropped instead — omission is
      // recoverable by opening the Sources panel; misattribution is not.
      const usesCiteIndex = rawSources.some((src) => typeof src?.cite_index === 'number');
      const citeIndexToRaw = new Map<number, (typeof rawSources)[number]>();
      rawSources.forEach((src, idx) => {
        // In a stamped array, an unstamped entry (e.g. an officeholder profile
        // attached after the stream) has no citation number of its own, and
        // letting it claim its position would hand it another source's marker.
        if (typeof src?.cite_index !== 'number' && usesCiteIndex) return;
        const ci = typeof src?.cite_index === 'number' ? src.cite_index : idx + 1;
        if (!citeIndexToRaw.has(ci)) citeIndexToRaw.set(ci, src);
      });

      const filteredSources: typeof parsedMessage.sources = [];
      const dedupedIdxToNewNum = new Map<number, number>();
      const map = new Map<number, number>();
      let newIndex = 1;

      const regex = /\[(\d+)\]/g;
      let match;
      while ((match = regex.exec(text)) !== null) {
        const originalNum = parseInt(match[1], 10);
        if (map.has(originalNum)) continue;

        const rawSource = citeIndexToRaw.get(originalNum);
        const rawUrl = typeof rawSource?.url === 'string' ? rawSource.url.trim() : '';
        if (!rawUrl) continue;

        const dedupedIdx = dedupedUrlToIdx.get(urlDedupKey(normalizeUrl(rawUrl)));
        if (dedupedIdx === undefined) continue;

        let assignedNewNum: number;
        if (!dedupedIdxToNewNum.has(dedupedIdx)) {
          assignedNewNum = newIndex++;
          dedupedIdxToNewNum.set(dedupedIdx, assignedNewNum);
          filteredSources.push(parsedMessage.sources[dedupedIdx]);
        } else {
          assignedNewNum = dedupedIdxToNewNum.get(dedupedIdx)!;
        }
        map.set(originalNum, assignedNewNum);
      }

      // The menu lists the sources the answer cites, and only those. A turn
      // ships every result its tools returned — a search alone can carry ten,
      // most of them read past — so listing the uncited ones told the voter the
      // answer stood on fifteen pages when it rested on four, and gave them no
      // way to tell which was which. Every row here answers to a chip in the
      // body, and `repairCitations` above has already put a chip on each claim
      // this turn holds a source for.
      //
      // Rows stay in the order the body cites them, so `groupSources` gives a
      // row the same group ordinal the chip pointing at it prints.
      return {
        citedSources: filteredSources,
        panelSources: filteredSources,
        // Every row is cited now, so the whole list is numbered. Kept as a
        // count rather than dropped: `SourceList` takes it as the boundary
        // between cited rows and provenance, and that is a contract the panel
        // shares with mobile and the widget.
        citedCount: filteredSources.length,
        citationNumberMap: map,
        citationsResolved: filteredSources.length > 0,
      };
    }, [bodyText, parsedMessage, message.sources]);

  // Replace [n] citations with PUA-wrapped sentinels. The real <sup><a>
  // citation chips are built at the AST level by rehypeRewrite below.
  // Emitting raw HTML into the markdown source breaks inside GFM table cells:
  // bare URLs in `href` trigger the autolink-literal extension, and literal
  // "|" in `aria-label` gets parsed as a column separator. Both produce
  // visible breakage that depends on per-citation content (see #1777).
  // ALWAYS replace even when the sources array is empty, so the markdown
  // parser doesn't see naked `[n]` and treat them as reference-link labels
  // (which would render as `[[n]]`).
  const contentWithCitations = useMemo(() => {
    const text = bodyText || '';
    // Citations that don't map to any deduped source (e.g. raw entry with
    // malformed or missing URL) are dropped — falling back to the raw LLM
    // index would surface non-sequential numbers like "[14]" between "[4]"
    // and "[5]".
    // A run of adjacent markers is matched with the single space in front of
    // it, so a run that resolves to nothing takes that space with it: "…Act
    // [9]." reads "…Act." rather than "…Act .". Dropping is common now that an
    // unresolved marker no longer falls back to a position, which is what makes
    // the stray space worth handling. Matched as a run because "[9][10]" with
    // only [10] resolving still needs the space its chip sits in.
    return text.replace(/([ \t]?)((?:\[\d+\])+)/g, (_m, gap: string, markers: string) => {
      const chips = (markers.match(/\[\d+\]/g) ?? [])
        .map((marker) => citationNumberMap.get(parseInt(marker.slice(1, -1), 10)))
        .filter((n): n is number => n !== undefined)
        .map((n) => `${CITE_OPEN}${n}${CITE_CLOSE}`)
        .join('');
      return chips ? `${gap}${chips}` : '';
    });
  }, [bodyText, citationNumberMap]);

  // Render the citation placeholders that rehypeRewrite left in the tree.
  // Chips live in React rather than in the HAST so one component owns the
  // favicon, the ordinal, the hover preview, and the cluster's expand state;
  // every other <span> in the markdown passes straight through.
  const markdownComponents = useMemo(
    () => ({
      span: (props: any) => {
        const spanProps = { ...props };
        delete spanProps.node;
        const nums = citeNumsFromClassName(props.className);
        if (!nums) return <span {...spanProps} />;
        return <CitationCluster nums={nums} citedSources={citedSources} t={t} />;
      },
    }),
    [citedSources, t]
  );

  // Removed auto-scroll during streaming to prevent unwanted scrolling

  const renderAvatar = () => {
    return (
      <Box
        sx={{
          animation: 'fadeInScale 0.4s ease-out',
          '@keyframes fadeInScale': {
            '0%': {
              opacity: 0,
              transform: 'scale(0.8)',
            },
            '100%': {
              opacity: 1,
              transform: 'scale(1)',
            },
          },
        }}
      >
        <VoteMateLogo size={32} />
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" mb={2}>
      {/* While loading: thinking logo on the left, narration + patient
          message stacked on the right. Status lines grow as new narration
          arrives and disappear entirely once streaming completes — only the
          finished body remains. */}
      {isLastMessage && message.isLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            px: 2,
            pb: 1,
          }}
        >
          <Box sx={{ pt: 0.25, flexShrink: 0 }}>
            <MinimalThinkingState size={32} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <NarrationTimeline events={narrationEvents} language={message.trailLanguage} />
          </Box>
        </Box>
      )}

      {/* The turn's research trail, kept after the answer lands. Collapsed by
          default so it never pushes the answer down the page; expanding it is
          how a reader checks which sources the reasoning actually leaned on.
          Offered whenever the turn actually researched — the tool steps are the
          trail, so gating on reasoning alone hid it on every turn that searched
          without emitting a summary. A turn answered from context has no steps
          and no reasoning, so it still gets no control. */}
      {!message.isLoading && hasResearchTrail(narrationEvents) && (
        <ReasoningDisclosure
          events={narrationEvents}
          px={MESSAGE_BODY_PX}
          answerDepth={message.answerDepth}
          researchDepth={message.researchDepth}
          language={message.trailLanguage}
        />
      )}

      {/* "Memory updated" pill — frames the assistant's response as
          "I noted this, here's what I have to say." Lives above the
          body so the user sees the retention acknowledgment first,
          matching Claude.ai's UX. Only renders when the backend
          persisted at least one new durable memory from this turn,
          and only after streaming completes (so it doesn't flicker
          mid-response). Indented to align with the message body's
          left edge; bottom margin gives it breathing room from the
          response below. */}
      {!message.isLoading && message.memoryUpdated && (
        <Box
          role="status"
          aria-label={t('memory.updatedAriaLabel')}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            alignSelf: 'flex-start',
            mx: MESSAGE_BODY_PX,
            mb: 1.5,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            backgroundColor: semanticColors.backgroundSecondary,
            color: semanticColors.textCaption,
            fontSize: 11,
            lineHeight: 1.4,
            animation: 'memoryPillIn 0.3s ease-out',
            '@keyframes memoryPillIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(-2px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <CheckCircleIcon style={{ width: 12, height: 12, flexShrink: 0 }} />
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: 'inherit' }}>
            {t('memory.updatedPill')}
          </Typography>
        </Box>
      )}

      {/* Chat bubble - show content while streaming, no animation.
          Skip entirely when bodyText is empty (during narration-only
          streaming) so we don't render an empty white box. */}
      {bodyText && (
        <Box
          data-testid="bot-message-body"
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Paper
            elevation={0}
            // Stable hook for the synthetic chat monitor, same role as
            // chat-input / send-button on the composer. An assistant reply and
            // a user message are both a bare MUI Paper, so without this the
            // monitor has to infer which is which from the text — and a real
            // answer opening with a heading like "What a Provisional Ballot
            // Is" got read as the user's own question and reported as "no bot
            // response". Purely a test hook; it renders nothing.
            data-testid="bot-message"
            sx={{
              background: 'transparent',
              borderRadius: BORDER_RADIUS,
              width: '100%',
              ...sx,
            }}
          >
            <Box>
              <Box
                sx={{
                  background: 'transparent',
                  borderRadius: BORDER_RADIUS,
                  p: MESSAGE_BODY_PX,
                  opacity: message.isLoading && !message.text ? 0 : 1,
                  animation:
                    message.isLoading && !message.text
                      ? 'fadeIn 0.5s ease-in 0.2s forwards'
                      : 'none',
                  '@keyframes fadeIn': {
                    '0%': {
                      opacity: 0,
                    },
                    '100%': {
                      opacity: 1,
                    },
                  },
                }}
              >
                {/* Thinking state is now shown via the logo below */}
                <Typography
                  color={primitiveColors.rockSlate}
                  component="div"
                  sx={{
                    width: '100%',
                    ...sx,
                    '& p': {
                      margin: 0,
                    },
                  }}
                >
                  {/* Use MarkdownPreview for all content - it handles HTML and markdown */}
                  <MarkdownPreview
                    source={contentWithCitations}
                    components={markdownComponents}
                    // remark-gfm defaults to singleTilde:true, so a lone "~" (the
                    // model's "approximately ~$50k") strikes through to the next
                    // "~". The lib appends its own gfm last; reconfigure it so
                    // only "~~double~~" strikes through.
                    pluginsFilter={(type, plugins) => {
                      if (type !== 'remark') return plugins;
                      const out = [...plugins];
                      const last = out[out.length - 1];
                      if (typeof last === 'function') {
                        out[out.length - 1] = [last, { singleTilde: false }];
                      }
                      return out;
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      ...sx,
                      color: primitiveColors.rockSlate,
                    }}
                    data-color-mode="light"
                    rehypeRewrite={(node, index, parent) => {
                      if (!node || typeof node !== 'object') return;

                      // Move each block's citations into one de-duplicated
                      // trailing group so chips cluster at the end of the
                      // paragraph/bullet/cell instead of interrupting the
                      // sentence (and a source cited three times shows once).
                      // Runs before the inline fallback below and strips the
                      // sentinels it relocates, so the fallback only handles
                      // stray citations outside a recognized block.
                      if (
                        node.type === 'element' &&
                        CITATION_BLOCK_TAGS.has((node as { tagName: string }).tagName)
                      ) {
                        relocateCitationsToBlockEnd(node, citedSources);
                      }

                      // Expand citation sentinels (CITE_OPEN N CITE_CLOSE)
                      // produced by contentWithCitations into <sup><a> HAST
                      // nodes spliced into the parent's children. Doing this
                      // here — after markdown parsing — means citations
                      // render identically inside table cells, list items,
                      // blockquotes, etc., without the inline-HTML hazards
                      // that broke the previous approach (see #1777).
                      if (
                        node.type === 'text' &&
                        typeof node.value === 'string' &&
                        node.value.indexOf(CITE_OPEN) !== -1 &&
                        parent &&
                        typeof index === 'number'
                      ) {
                        const value = node.value;
                        const newChildren: unknown[] = [];
                        let lastIdx = 0;
                        for (const m of value.matchAll(CITE_SENTINEL_RE)) {
                          const matchIndex = m.index ?? 0;
                          if (matchIndex > lastIdx) {
                            newChildren.push({
                              type: 'text',
                              value: value.slice(lastIdx, matchIndex),
                            });
                          }
                          const n = parseInt(m[1], 10);
                          newChildren.push(buildCitationHastNode([n]));
                          lastIdx = matchIndex + m[0].length;
                        }
                        if (lastIdx < value.length) {
                          newChildren.push({
                            type: 'text',
                            value: value.slice(lastIdx),
                          });
                        }
                        const parentChildren = (parent as { children: unknown[] }).children;
                        if (Array.isArray(parentChildren) && newChildren.length > 0) {
                          parentChildren.splice(index, 1, ...newChildren);
                        }
                        return;
                      }

                      // Clean literal * and # from text nodes that the markdown
                      // parser couldn't resolve (malformed bold/header syntax).
                      // Link and code text is already resolved, and stripping
                      // there corrupts real content: an autolinked polling-place
                      // URL like `https://voterstatus.sos.ca.gov/#/` would lose
                      // its fragment from the visible label while the href kept
                      // it, so the reader sees an address that isn't the one the
                      // link opens.
                      const parentTag = (parent as { tagName?: string } | undefined)?.tagName;
                      if (
                        node.type === 'text' &&
                        typeof node.value === 'string' &&
                        parentTag !== 'a' &&
                        parentTag !== 'code'
                      ) {
                        // Strip leading/trailing ** or * (orphaned bold/italic markers)
                        node.value = node.value
                          .replace(/^\*{1,2}|\*{1,2}$/g, '')
                          // Strip # markers that ended up as literal text
                          .replace(/#{1,6}\s*/g, '');
                      }

                      if (node.type !== 'element') return;

                      // Ensure all external links open in new tab
                      if (node.tagName === 'a') {
                        const href = node.properties?.href as string;
                        if (href && (href.startsWith('http') || href.startsWith('https'))) {
                          node.properties = {
                            ...node.properties,
                            target: '_blank',
                            rel: 'noopener noreferrer',
                          };
                        }
                      }
                      // Add spacing between paragraphs
                      if (node.tagName === 'p') {
                        const existingStyle = node.properties?.style || '';
                        node.properties = {
                          ...node.properties,
                          style: `${existingStyle} margin-bottom: 1em`,
                        };
                      }
                      // Fix table overflow
                      if (node.tagName === 'table') {
                        const existingStyle = node.properties?.style || '';
                        node.properties = {
                          ...node.properties,
                          style: `${existingStyle} display: block; overflow-x: auto; width: 100%;`,
                        };
                      }
                    }}
                  />
                </Typography>
                {/* Only show message actions when not loading, and only when
                    the turn produced an answer — a stopped turn with no text
                    has nothing to rate or copy. */}
                {!message.isLoading && !message.noAnswer && (
                  <MessageActions
                    messageText={message.text}
                    onFeedbackSubmit={(feedbackType, feedback) =>
                      onFeedbackSubmit?.(message.id, feedbackType, feedback)
                    }
                    showConfirmation={feedbackConfirmations?.[message.id]}
                    sources={panelSources}
                    sourcesAreNumbered={citationsResolved}
                    citedCount={citedCount}
                    conversationId={conversationId}
                    messageId={message.id}
                    userId={userId}
                  />
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Static avatar below the response — only on the last completed bot message.
          While loading, the thinking state is rendered at the top next to the
          patient message instead of here. */}
      {isLastMessage && !message.isLoading && <Box sx={{ px: 2, pt: 0.5 }}>{renderAvatar()}</Box>}
    </Box>
  );
};
